import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ElementIdGenerationService} from '@app/shared/services';
import {SustainabilityRating, SustainabilityRatingTypeId} from '@app/shared/services/api/sustainability-ratings/ratings/sustainability-ratings.interface';
import {SustainabilityRatingSearchTerm, SustainabilityRatingsService} from '@app/shared/services/api/sustainability-ratings/ratings/sustainability-ratings.service';
import {finalize, of, Subscription} from 'rxjs';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {RATING_DATE_FORMAT} from '@app/layout/dashboard/ratings/shared.interface';
import {map} from "rxjs/operators";


interface RatingOption extends SustainabilityRating {
  latest?: boolean;
}


@Component({
  selector: 'app-sustainability-rating-select',
  templateUrl: './sustainability-rating-select.component.html',
  styleUrls: ['./sustainability-rating-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SustainabilityRatingSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SustainabilityRatingSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() loading = false;
  @Input() clearable = false;
  @Input() groupByFn: Function;
  @Input() compareFn: (rating1: SustainabilityRating, rating2: SustainabilityRating) => number;
  @Input() ratings: SustainabilityRating[];
  @Input() displayTypeNameOnly = false;
  @Input() searchCriteria: SustainabilityRatingSearchTerm;

  @Output() dataLoaded = new EventEmitter<SustainabilityRating[]>();

  @ViewChild(AceSelectComponent, {static: true})
  private aceSelect: AceSelectComponent;

  readonly DATE_FORMAT = RATING_DATE_FORMAT;

  elementId: string;
  ratingOptions: RatingOption[];
  ratingsGroupByFn: Function;

  private fetchRatingsSubscription: Subscription;

  constructor(private sustainabilityRatingsService: SustainabilityRatingsService, elementIdGenerationService: ElementIdGenerationService) {
    this.elementId = elementIdGenerationService.buildIdGenerator().getId('sustainability-rating');
  }

  ngOnInit(): void {
    this.validateSearchCriteria();
    this.checkAndSetGroupByFn();
    this.loadRatingOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.displayTypeNameOnly || changes.searchCriteria) {
      this.validateSearchCriteria();
    }
    if (changes.displayTypeNameOnly || changes.groupByFn) {
      this.checkAndSetGroupByFn();
    }
    if (changes.searchCriteria || changes.ratings) {
      this.loadRatingOptions();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeFetchRatings();
  }

  writeValue(obj: RatingOption): void {
    this.aceSelect.writeValue(obj);
  }

  registerOnChange(fn: (rating: RatingOption) => void): void {
    this.aceSelect.registerOnChange(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.aceSelect.registerOnTouched(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  asRatingOption(rating: RatingOption): RatingOption {
    return rating;
  }

  private checkAndSetGroupByFn() {
    this.ratingsGroupByFn = this.groupByFn ?? (
      this.displayTypeNameOnly ? null : this.groupByTypeFn
    );
  }

  private validateSearchCriteria() {
    const limitPerSiteAndType = this.searchCriteria?.limit_per_site_and_type;
    if (limitPerSiteAndType != null) {
      if (limitPerSiteAndType <= 0) {
        throw new Error('limit_per_site_and_type in search criteria cannot be set to 0 or less.');
      }
      if (limitPerSiteAndType > 1 && this.displayTypeNameOnly) {
        throw new Error('Cannot set displayTypeNameOnly to true while limit_per_site_and_type in search criteria is greater than 1.')
      }
    }
  }

  private toRatingOptions(ratings: SustainabilityRating[]): RatingOption[] {
    const ratingTypeIds = new Set<SustainabilityRatingTypeId>();
    return ratings.map(rating => {
      let ratingOption: RatingOption = {...rating};
      if (!ratingTypeIds.has(rating.sustainability_rating_type_id)) {
        ratingOption.latest = true;
        ratingTypeIds.add(rating.sustainability_rating_type_id);
      }
      return ratingOption;
    });
  }

  private unsubscribeFetchRatings() {
    if (this.fetchRatingsSubscription) {
      this.fetchRatingsSubscription.unsubscribe();
      this.fetchRatingsSubscription = null;
    }
  }

  private loadRatingOptions() {
    this.unsubscribeFetchRatings();
    if (this.searchCriteria == null) {
      return;
    } else {
      this.loading = true;
      const getRatings$ = this.ratings != null
        ? of(this.ratings)
        : this.sustainabilityRatingsService.getAllSustainabilityRatings(this.searchCriteria, {includeType: true})
          .pipe(map(ratings => {
            if (ratings?.length) {
              return this.compareFn ? ratings.sort(this.compareFn) : ratings;
            } else {
              return [];
            }
          }));
      this.fetchRatingsSubscription = getRatings$.pipe(finalize(() => this.loading = false)).subscribe(ratings => {
        this.ratings = ratings;
        this.ratingOptions = this.toRatingOptions(ratings);
        this.dataLoaded.emit(ratings);
      });
    }
  }

  private groupByTypeFn(rating: SustainabilityRating): string {
    return rating.sustainability_rating_type.name;
  }
}
